// src/badges/UserStatusBadge.tsx
import { Chip } from "@mui/material";

// src/badges/styles.ts
function outlinedBadgeSx(color) {
  if (color === "default") {
    return {
      borderWidth: 1,
      borderStyle: "solid",
      borderColor: "text.secondary",
      bgcolor: "transparent",
      color: "text.secondary",
      "& .MuiChip-label": { color: "text.secondary" },
      "& .MuiChip-icon": { color: "text.secondary" }
    };
  }
  return {
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: `${color}.main`,
    bgcolor: "transparent",
    color: `${color}.main`,
    "& .MuiChip-label": { color: `${color}.main` },
    "& .MuiChip-icon": { color: `${color}.main` }
  };
}
function getOutlinedBadgeChipProps(label, color = "default") {
  return {
    label,
    variant: "outlined",
    sx: outlinedBadgeSx(color)
  };
}
function mergeChipSx(base, extra) {
  if (!extra) return base;
  return {
    ...base,
    sx: [base.sx, extra].filter(Boolean)
  };
}
function getUserStatusChipProps(status) {
  const normalized = status;
  switch (normalized) {
    case "Verified":
    case "Registered":
      return getOutlinedBadgeChipProps(status, "success");
    case "New":
      return getOutlinedBadgeChipProps(status, "warning");
    case "Disactivated":
    case "Banned":
      return getOutlinedBadgeChipProps(status, "error");
    default:
      if (status === "Blocked") {
        return getOutlinedBadgeChipProps(status, "error");
      }
      return getOutlinedBadgeChipProps(status, "default");
  }
}
function getUserRoleChipProps(role) {
  const normalized = role;
  switch (normalized) {
    case "Admin":
      return getOutlinedBadgeChipProps(role, "warning");
    case "Teacher":
      return getOutlinedBadgeChipProps(role, "secondary");
    case "Student":
    default:
      return getOutlinedBadgeChipProps(role || "Student", "primary");
  }
}
function getUserRoleChipPropsWithIconStyle(role) {
  return mergeChipSx(getUserRoleChipProps(role), {
    fontWeight: "bold",
    "& .MuiChip-icon": { ml: 0.5 }
  });
}
function getContentStatusChipProps(status) {
  const normalized = (status || "draft").toLowerCase();
  switch (normalized) {
    case "published":
    case "active":
      return getOutlinedBadgeChipProps(
        normalized === "active" ? "Active" : status,
        "success"
      );
    case "archived":
      return getOutlinedBadgeChipProps(status, "default");
    case "draft":
    default:
      return getOutlinedBadgeChipProps(status || "draft", "default");
  }
}

// src/badges/statusIcons.tsx
import {
  AdminPanelSettings,
  Cancel,
  CheckCircle,
  NewReleases,
  Person,
  School
} from "@mui/icons-material";
import { jsx } from "react/jsx-runtime";
function getUserStatusIcon(status) {
  switch (status) {
    case "Registered":
    case "Verified":
      return /* @__PURE__ */ jsx(CheckCircle, {});
    case "New":
      return /* @__PURE__ */ jsx(NewReleases, {});
    case "Disactivated":
    case "Banned":
    case "Blocked":
      return /* @__PURE__ */ jsx(Cancel, {});
    default:
      return /* @__PURE__ */ jsx(Person, {});
  }
}
function getUserRoleIcon(role) {
  switch (role) {
    case "Admin":
      return /* @__PURE__ */ jsx(AdminPanelSettings, {});
    case "Teacher":
      return /* @__PURE__ */ jsx(School, {});
    case "Student":
    default:
      return /* @__PURE__ */ jsx(Person, {});
  }
}

// src/badges/UserStatusBadge.tsx
import { jsx as jsx2 } from "react/jsx-runtime";
var UserStatusBadge = ({
  status,
  size = "small",
  showIcon = false
}) => {
  if (!status) return null;
  const chipProps = getUserStatusChipProps(status);
  const icon = showIcon ? getUserStatusIcon(status) : void 0;
  return /* @__PURE__ */ jsx2(
    Chip,
    {
      size,
      ...chipProps,
      icon,
      sx: { fontWeight: showIcon ? "bold" : void 0, ...chipProps.sx }
    }
  );
};

// src/badges/UserActivityBadge.tsx
import { Chip as Chip2 } from "@mui/material";
import { jsx as jsx3 } from "react/jsx-runtime";
var DEFAULT_INACTIVE_DAYS = 30;
function isUserRecentlyActive(lastConnected, inactiveDays = DEFAULT_INACTIVE_DAYS) {
  if (!lastConnected) return false;
  const cutoff = Date.now() - inactiveDays * 24 * 60 * 60 * 1e3;
  return new Date(lastConnected).getTime() >= cutoff;
}
var UserActivityBadge = ({
  lastConnected,
  inactiveDays = DEFAULT_INACTIVE_DAYS,
  size = "small"
}) => {
  const active = isUserRecentlyActive(lastConnected, inactiveDays);
  const chipProps = active ? getOutlinedBadgeChipProps("Active", "success") : getOutlinedBadgeChipProps("Inactive", "default");
  return /* @__PURE__ */ jsx3(Chip2, { size, ...chipProps });
};

// src/badges/UserRoleBadge.tsx
import { Chip as Chip3 } from "@mui/material";
import { jsx as jsx4 } from "react/jsx-runtime";
var UserRoleBadge = ({
  role,
  size = "small",
  showIcon = false
}) => {
  const label = role || "Student";
  const chipProps = showIcon ? getUserRoleChipPropsWithIconStyle(label) : getUserRoleChipProps(label);
  const icon = showIcon ? getUserRoleIcon(label) : void 0;
  return /* @__PURE__ */ jsx4(Chip3, { size, ...chipProps, icon });
};

// src/badges/ContentStatusBadge.tsx
import { Chip as Chip4 } from "@mui/material";
import { jsx as jsx5 } from "react/jsx-runtime";
var ContentStatusBadge = ({
  status,
  size = "small"
}) => {
  const label = status || "draft";
  const chipProps = getContentStatusChipProps(label);
  return /* @__PURE__ */ jsx5(Chip4, { size, ...chipProps, label: chipProps.label ?? label });
};

// src/badges/OutlinedBadge.tsx
import { Chip as Chip5 } from "@mui/material";
import { jsx as jsx6 } from "react/jsx-runtime";
var OutlinedBadge = ({
  label,
  color = "default",
  size = "small",
  onClick,
  sx
}) => {
  const chipProps = getOutlinedBadgeChipProps(label, color);
  return /* @__PURE__ */ jsx6(
    Chip5,
    {
      size,
      ...chipProps,
      onClick,
      sx: [chipProps.sx, sx].filter(Boolean)
    }
  );
};
export {
  ContentStatusBadge,
  OutlinedBadge,
  UserActivityBadge,
  UserRoleBadge,
  UserStatusBadge,
  getContentStatusChipProps,
  getOutlinedBadgeChipProps,
  getUserRoleChipProps,
  getUserRoleChipPropsWithIconStyle,
  getUserStatusChipProps,
  isUserRecentlyActive,
  outlinedBadgeSx
};
